import { useInfiniteQuery, useQuery } from "react-query";
import { ApiError, api } from "utils/api";
import { searchKeys } from "./queryKeys";
import type { SearchRequest, SearchResponse } from "../types";

// SORT - Pass an array of field names to sort. Add "desc" after to sort descending
// sort: ['mintStatus', 'mintDate desc']

const postSearch = (request?: SearchRequest) => {
  if (!request) {
    return Promise.resolve({ searchAfter: "", data: [], totalResults: 0 });
  }
  return api.post<SearchResponse>("/projects", {
    body: JSON.stringify({
      perPage: 5,
      ...request,
      type: request.type ? [request.type] : undefined,
    }),
  });
};

/**
 * Custom hook to search for Reviewables and manage state
 */
const useSearch = (params: SearchRequest) =>
  useQuery(searchKeys.default(params), () => postSearch(params));

/**
 * Custom hook to search for Reviewables with infinite pagination and manage state
 */
const useInfiniteSearch = (params: SearchRequest) =>
  useInfiniteQuery<SearchResponse, ApiError>(
    searchKeys.infinite(params),
    ({ pageParam }) => postSearch({ searchAfter: pageParam, ...params }),
    {
      getNextPageParam: (lastPage: SearchResponse) => lastPage.searchAfter,
    }
  );

/**
 * Custom hook to search for Reviewable by name incrementally and manage state
 *
 * NOTE: If `name` is user input, make sure to debounce value with `useDebouncer()` util hook
 */
const useIncrementalSearch = (name: string, perPage = 4) =>
  useQuery<SearchResponse, ApiError, SearchResponse["data"]>(
    searchKeys.incremental({ name, perPage }),
    () => postSearch({ incremental: true, name, perPage }),
    {
      enabled: !!name,
      select: (res) => res.data,
      keepPreviousData: true,
    }
  );

const useGetMintStatusReport = () =>
  useQuery<SearchResponse, ApiError>(["project", "mint-status-report"], () =>
    postSearch({
      mintStatus: ["Not yet minted", "currently minting"],
      sort: ["mintDate"],
      perPage: 100,
    })
  );

export {
  useGetMintStatusReport,
  useIncrementalSearch,
  useInfiniteSearch,
  useSearch,
};
